import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useRestaurantContext } from '../../../../contexts/RestaurantContext';
import { useDebouncedDesignSave, UseDebouncedDesignSaveOptions } from '../../../../hooks/useDebouncedDesignSave';
import useFlushWhenTabInactive from './useFlushWhenTabInactive';

/* eslint-disable no-unused-vars -- callback params are type-only */
export type DesignTabLoadActions<TState, TPayload> = {
  setState: Dispatch<SetStateAction<TState>>;
  setSettingsError: (error: string | null) => void;
  markPersisted: (payload: TPayload) => void;
  setSettingsReady: (ready: boolean) => void;
};

type UseDesignTabSettingsOptions<TState, TPayload> = {
  isActive: boolean;
  initialState: TState | (() => TState);
  toPayload: (state: TState) => TPayload;
  save: Omit<UseDebouncedDesignSaveOptions<TPayload>, 'enabled'>;
  load: (restaurantId: number, actions: DesignTabLoadActions<TState, TPayload>) => Promise<void>;
};
/* eslint-enable no-unused-vars */

const useDesignTabSettings = <TState, TPayload>({
  isActive,
  initialState,
  toPayload,
  save: saveOptions,
  load
}: UseDesignTabSettingsOptions<TState, TPayload>) => {
  const { currentRestaurant } = useRestaurantContext();
  const restaurantId = currentRestaurant?.restaurantID;

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsReady, setSettingsReady] = useState(false);
  const [state, setState] = useState(initialState);

  const apiPayload = useMemo(() => toPayload(state), [state, toPayload]);

  const { flushPendingSave, markPersisted } = useDebouncedDesignSave(settingsReady ? apiPayload : null, {
    ...saveOptions,
    enabled: settingsReady && !!restaurantId
  });

  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;

  const markPersistedRef = useRef(markPersisted);
  markPersistedRef.current = markPersisted;

  const loadRef = useRef(load);
  loadRef.current = load;

  useFlushWhenTabInactive(isActive, flushPendingSave);

  useEffect(() => {
    if (!restaurantId) {
      setSettingsReady(false);
      setSettingsLoading(false);
      return undefined;
    }

    let canceled = false;

    setSettingsLoading(true);
    setSettingsError(null);
    setSettingsReady(false);

    const actions: DesignTabLoadActions<TState, TPayload> = {
      setState,
      setSettingsError,
      markPersisted: (payload) => markPersistedRef.current(payload),
      setSettingsReady
    };

    loadRef.current(restaurantId, actions).finally(() => {
      if (!canceled) {
        setSettingsLoading(false);
      }
    });

    return () => {
      canceled = true;
      flushPendingSaveRef.current();
    };
  }, [restaurantId]);

  return {
    settingsLoading,
    settingsError,
    state,
    setState
  };
};

export default useDesignTabSettings;
